import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { serviceSlice } from "../../app/serviceSlice";
import { getSelectedTrial } from "../auth/login/loginSlice";

export interface Tag {
    label: string;
    uuid: string;
    color: string;
}
export interface IPatient {
    isQc: boolean;
    earliestISODate: string;
    dateOfBirth: string;
    lastUploadedOnIso: string;
    gender: string;
    group: string;
    label: string;
    latestISODate: string;
    siteName: string;
    patientId: string;
    firstName?: string;
    lastName?: string;
    uuid: string;
    site: string;
    visits: number;
    tags: Tag[];
    laterality: "OS" | "OD" | "OU" | undefined;
    isRemoved: boolean;
    workflowStates: {
        label: string;
        value: string;
        priority?: string;
        order?: number;
    }[];
}

export interface PatientsState {
    allPatientsList: IPatient[];
    patientsList: IPatient[];
    searchQry?: string;
    filteredPatientsCount: number;
    sort: { field: keyof IPatient; order: string };
    status: "idle" | "success" | "loading" | "failed";
    errorMessage?: string;
    currentPatient?: string;
}

const LOCAL_STORAGE_SORT_KEY = "sortKey";

const VALID_SORT_FIELDS: (keyof IPatient)[] = [
    "isQc",
    "patientId",
    "lastName",
    "site",
    "gender",
    "dateOfBirth",
    "visits",
    "latestISODate",
    "lastUploadedOnIso",
];

const DEFAULT_SORT = {
    field: "patientId" as keyof IPatient,
    order: "asc",
};

const getSortOrderFromLocalStorage = () => {
    const sort = localStorage.getItem(LOCAL_STORAGE_SORT_KEY);

    if (!sort) return DEFAULT_SORT;

    try {
        const parsedSort = JSON.parse(sort);
        const field = parsedSort?.field as keyof IPatient;
        const order = parsedSort?.order;

        if (!VALID_SORT_FIELDS.includes(field)) return DEFAULT_SORT;
        if (order !== "asc" && order !== "desc") return DEFAULT_SORT;

        return { field, order };
    } catch {
        return DEFAULT_SORT;
    }
};
export const getInitialState: () => PatientsState = () => ({
    allPatientsList: [],
    patientsList: [],
    filteredPatientsCount: 0,
    sort: getSortOrderFromLocalStorage(),
    status: "loading",
});

export const patientsSlice = createSlice({
    name: "patients",
    initialState: getInitialState,
    reducers: {
        selectPatient: (state, action: PayloadAction<{ currentPatientId: string }>) => {
            state.currentPatient = action.payload.currentPatientId;
        },
        deselectPatient: (state) => {
            state.currentPatient = undefined;
        },
        sortPatients: (state, action: PayloadAction<any>) => {
            const newSortConfig = { field: action.payload.field, order: action.payload.order };
            localStorage.setItem(LOCAL_STORAGE_SORT_KEY, JSON.stringify(newSortConfig));
            state.sort = newSortConfig;
        },
        setFilteredPatientsCount(state, action: PayloadAction<number>) {
            state.filteredPatientsCount = action.payload;
        },
    },
});

export const { selectPatient, sortPatients, deselectPatient, setFilteredPatientsCount } = patientsSlice.actions;

export const selectPatients = (state: RootState) => {
    return state.patients;
};

export const selectPatientSort = (state: RootState) => {
    return state.patients.sort;
};

export const selectFilteredPatientsCount = (state: RootState) => {
    return state.patients.filteredPatientsCount;
};

export const selectCurrentPatient = createSelector(
    [
        (state: RootState) => state.patients.currentPatient,
        (state: RootState) => getSelectedTrial(state),
        (state: RootState) => state, // Pass the whole state as the third argument
    ],
    (currentPatient, trial, state) => {
        if (!currentPatient || !trial) return null;

        const queries = ((state as any)[serviceSlice.reducerPath]?.queries || {}) as Record<string, any>;
        const cachedPatients = Object.values(queries)
            .filter((query) => query?.endpointName === "getPatients")
            .filter((query) => query?.originalArgs?.trialId === trial.uuid)
            .flatMap((query) => query?.data?.patients || []);

        return cachedPatients.find((patient: IPatient) => patient.uuid === currentPatient) || null;
    }
);

export default patientsSlice.reducer;
